import React from "react";
import { useTranslation } from "react-i18next";
import useAppNavigate from "../../hooks/useAppNavigate";
import { hand } from "../../common/imagePath";
import {
  addBillsRecurringExpense,
  addBillsConnectBill,
  addBillsBankStatement,
  addBillsMore,
} from "../../common/imagePath";
import HelpfulTipsCard from "../../components/widgets/HelpfulTipsCard";
import StepRow from "../../components/widgets/StepRow";
import HelpfulCard from "../../components/widgets/HelpfulCard";
import PageHeader from "../../components/widgets/PageHeader";
import { BlueTickIcon, EllipseDotIcon } from "../../components/ui/icons";

const ROW_HEIGHT = 40;
const INNER_PAD_TOP = 8;
const HAND_SIZE = 60;

const AddBillsNavMockup = () => {
  const { t } = useTranslation();

  const NAV_ITEMS = [
    { icon: addBillsRecurringExpense, label: t("addBills.mockup.addBill"),        active: true  },
    { icon: addBillsConnectBill,      label: t("addBills.mockup.connectBank"),     active: false },
    { icon: addBillsBankStatement,    label: t("addBills.mockup.uploadStatement"), active: false },
    { icon: addBillsMore,             label: t("addBills.mockup.more"),            active: false },
  ];

  const activeIndex = NAV_ITEMS.findIndex((item) => item.active);
  const handTop =
    INNER_PAD_TOP + activeIndex * ROW_HEIGHT + ROW_HEIGHT / 2 - HAND_SIZE / 2;

  return (
    <div className="mx-4 mb-6">
      <style>{`
        @keyframes handTap {
          0%   { transform: translateX(0)     scale(1);    }
          35%  { transform: translateX(-10px) scale(0.92); }
          55%  { transform: translateX(-6px)  scale(0.96); }
          100% { transform: translateX(0)     scale(1);    }
        }
      `}</style>
      <div className="border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E] rounded-2xl p-6 relative overflow-visible">
        <div className="bg-white dark:bg-[#121212] border border-transparent dark:border-[#2A2F3A] rounded-xl px-2 py-2 relative overflow-visible min-w-[200px]">
          {NAV_ITEMS.map((item, index) => (
            <React.Fragment key={index}>
              <div
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 ${
                  index !== NAV_ITEMS.length - 1 ? "mb-0.5" : ""
                } ${item.active ? "bg-[#3479F7]" : ""}`}
              >
                <img
                  src={item.icon}
                  alt=""
                  className={`h-4 object-contain flex-shrink-0 ${item.active ? "brightness-0 invert" : ""}`}
                />
                <span
                  className={`text-sm font-semibold ${
                    item.active ? "text-white" : "text-[#1C1C1E] dark:text-white"
                  }`}
                >
                  {item.label}
                </span>
              </div>
              {index === 2 && (
                <div className="mx-3 my-0.5 h-[0.5px] bg-[#DEDEDE] dark:bg-[#2A2F3A]" />
              )}
            </React.Fragment>
          ))}

          <img
            src={hand}
            alt=""
            className="absolute pointer-events-none select-none"
            style={{
              width: HAND_SIZE,
              height: HAND_SIZE,
              top: handTop,
              right: -26,
              animation: "handTap 1.4s ease-in-out infinite",
              transformOrigin: "60% 30%",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const AddBills = () => {
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const overviewPoints = t("addBills.overviewPoints", { returnObjects: true });
  const steps = t("addBills.steps", { returnObjects: true });
  const tips = t("addBills.tips", { returnObjects: true });

  return (
    <div className="bg-[#F2F2F7] dark:bg-[#000000] transition-colors duration-200 pb-4">
      <PageHeader
        title={t("addBills.title")}
        onBack={() => navigate("/how-to-use")}
        titleClass="text-[14px]"
      />

      {/* Card 1 — Overview */}
      <div className="mx-4 mt-2 mb-4 rounded-2xl overflow-hidden border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E]">
        <div className="px-4 pt-4 pb-4">
          <h2 className="font-inter font-semibold text-[18px] text-black dark:text-white mb-1">
            {t("addBills.cardTitle")}
          </h2>
          <p className="text-xs lg:text-sm text-[#636363] dark:text-[#EBEBF5] dark:opacity-60 leading-relaxed mb-3 font-normal">
            {t("addBills.cardDesc")}
          </p>
          <div className="flex flex-col gap-2">
            {overviewPoints.map((point, i) => (
              <div key={i} className="flex items-center gap-2">
                <BlueTickIcon />
                <span className="text-xs lg:text-sm font-normal font-inter text-black dark:text-white leading-snug">
                  {point}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Card 2 — App Mockup */}
      <AddBillsNavMockup />

      {/* Section label */}
      <p className="px-4 mb-2 text-xs lg:text-sm font-semibold tracking-wide text-[##636363] uppercase">
        {t("addBills.sectionLabel")}
      </p>

      {/* Card 3 — Steps */}
      <div className="mx-4 mb-4 rounded-2xl overflow-hidden bg-white border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E]">
        <div className="px-4">
          {steps.map((step, i) => (
            <StepRow
              key={i}
              number={i + 1}
              title={step.title}
              desc={step.desc}
              isLast={i === steps.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Card 4 — Helpful Tips */}
      <HelpfulTipsCard
        tips={tips}
        BulletComponent={EllipseDotIcon}
        itemAlign="items-center"
      />

      {/* Card 5 — Was this helpful? */}
      <HelpfulCard />
    </div>
  );
};

export default AddBills;
